import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as loginApi, getCurrentUser } from '../services/authService';

export const AuthContext = createContext();

// A deep link the user tried to open before authenticating (set by
// DeepLinkResolver and the api.js 401 handler). Consumed once, after login.
const readReturnTo = () => {
  try {
    const value = sessionStorage.getItem('returnTo');
    if (!value) return null;
    if (!value.startsWith('/') || value.startsWith('//')) return null;
    if (value.startsWith('/login') || value.startsWith('/register')) return null;
    return value;
  } catch (e) {
    return null;
  }
};

const clearReturnTo = () => {
  try {
    sessionStorage.removeItem('returnTo');
  } catch (e) {
    // ignore
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (error) {
        localStorage.removeItem('token');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const getRedirectPath = (userData) => {
    // If mentor/student/counsellor hasn't completed onboarding, force them there
    if (!userData.details_added) {
      if (userData.role === 'mentor') return '/mentor/onboarding';
      if (userData.role === 'student') return '/student/onboarding';
      if (userData.role === 'counsellor') return '/counsellor/onboarding';
    }

    const roleRoutes = {
      'super_admin': '/super-admin/dashboard',
      'manager': '/manager/dashboard',
      'mentor': '/mentor/dashboard',
      'student': '/student/dashboard',
      'counsellor': '/counsellor/dashboard',
      'poc_owner': '/poc-owner/dashboard',
    };
    return roleRoutes[userData.role] || '/login';
  };

  const login = async (email, password) => {
    try {
      const response = await loginApi(email, password);
      localStorage.setItem('token', response.access_token);
      setUser(response.user);

      // A user still in onboarding must go there regardless of any saved deep link.
      const needsOnboarding =
        ['mentor', 'student', 'counsellor'].includes(response.user.role) &&
        !response.user.details_added;

      const returnTo = readReturnTo();
      clearReturnTo();

      const path = needsOnboarding
        ? getRedirectPath(response.user)
        : returnTo || getRedirectPath(response.user);
      navigate(path);

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const refreshUser = async () => {
    try {
      const userData = await getCurrentUser();
      setUser(userData);
      return userData;
    } catch (error) {
      return null;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    getRedirectPath,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isManager: user?.role === 'manager',
    isMentor: user?.role === 'mentor',
    isStudent: user?.role === 'student',
    isCounsellor: user?.role === 'counsellor',
    isPocOwner: user?.role === 'poc_owner'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};